import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CmrSettings from './CmrSettings';
import { DatalonSettings } from './DatalonSettings';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import type { OrganizationSettings, KoreseddelTemplate, BulkUploadJob, DriverMapping } from '../../types';

export default function Settings() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<Partial<OrganizationSettings>>({});
  const [autoUploadTemplates, setAutoUploadTemplates] = useState<string[]>([]);
  const [termsLinks, setTermsLinks] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);

  // Bulk upload state
  const [bulkFromDate, setBulkFromDate] = useState('');
  const [bulkTemplateId, setBulkTemplateId] = useState('');
  const [bulkSkipExisting, setBulkSkipExisting] = useState(true);
  const [bulkJobId, setBulkJobId] = useState<string | null>(null);
  const [bulkJobStatus, setBulkJobStatus] = useState<BulkUploadJob | null>(null);
  const [showErrorsDialog, setShowErrorsDialog] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  // e-conomic state
  const [economicTestResult, setEconomicTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isTestingEconomic, setIsTestingEconomic] = useState(false);
  // Journal-state ikke længere brugt — bogføring er deaktiveret. Beholdt setters
  // så test-forbindelse-flowet ikke skal omskrives; values smides bare væk.
  const [, setEconomicJournals] = useState<Array<{ journalNumber: number; name: string }>>([]);
  const [, setEconomicJournalsLoaded] = useState(false);

  // Danløn state
  const [driverMappings, setDriverMappings] = useState<DriverMapping[]>([]);
  const [danlonHasChanges, setDanlonHasChanges] = useState(false);
  const [isSavingMappings, setIsSavingMappings] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sync all DORA data
  const handleSyncFromDora = async () => {
    setIsSyncing(true);
    try {
      // First, clear the server-side cache
      await api.syncFromDora();

      // Then invalidate all DORA-related queries to force refetch from DORA API
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dora-teams'] }),
        queryClient.invalidateQueries({ queryKey: ['settings'] }),
        queryClient.invalidateQueries({ queryKey: ['koreseddel-templates'] }),
        queryClient.invalidateQueries({ queryKey: ['trailers'] }),
        queryClient.invalidateQueries({ queryKey: ['drivers'] }),
        queryClient.invalidateQueries({ queryKey: ['routes'] }),
      ]);
      setSaveMessage({ type: 'success', text: 'Data synkroniseret fra DORA' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Kunne ikke synkronisere data' });
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsSyncing(false);
    }
  };

  // Logo upload handlers
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setSaveMessage({ type: 'error', text: 'Kun billeder (JPEG, PNG, GIF, WebP) er tilladt' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      setSaveMessage({ type: 'error', text: 'Logo må maks. være 2MB' });
      setTimeout(() => setSaveMessage(null), 3000);
      return;
    }

    setIsUploadingLogo(true);
    try {
      await api.uploadLogo(file);
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      // Update preview from the uploaded file directly
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
      setLogoPreviewUrl(URL.createObjectURL(file));
      setSaveMessage({ type: 'success', text: 'Logo uploadet!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Kunne ikke uploade logo' });
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsUploadingLogo(false);
      // Reset input so same file can be selected again
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    setIsUploadingLogo(true);
    try {
      await api.deleteLogo();
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setLogoPreviewUrl(null);
      setSaveMessage({ type: 'success', text: 'Logo slettet' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Kunne ikke slette logo' });
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleViewTemplatePreview = async (templateId: string) => {
    setPreviewLoading(templateId);
    try {
      await api.viewTemplatePreview(templateId);
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Kunne ikke åbne preview' });
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setPreviewLoading(null);
    }
  };

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.getSettings(),
  });

  const { data: templates } = useQuery({
    queryKey: ['koreseddel-templates'],
    queryFn: () => api.getKoreseddelTemplates(),
  });

  const { data: teams } = useQuery({
    queryKey: ['dora-teams'],
    queryFn: () => api.getTeams(),
  });

  const { data: driverMappingsData } = useQuery({
    queryKey: ['driver-mappings'],
    queryFn: () => api.getDriverMappings(),
  });

  const { data: fmsDevices } = useQuery({
    queryKey: ['fms-devices'],
    queryFn: () => api.getFmsDevices(),
    enabled: !!settings?.fmsApiKey && !!settings?.fmsCustomerId,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<OrganizationSettings>) => api.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setHasChanges(false);
      setSaveMessage({ type: 'success', text: 'Indstillinger gemt!' });
      setTimeout(() => setSaveMessage(null), 3000);
    },
    onError: () => {
      setSaveMessage({ type: 'error', text: 'Kunne ikke gemme indstillinger' });
    },
  });

  useEffect(() => {
    if (driverMappingsData) {
      setDriverMappings(driverMappingsData);
    }
  }, [driverMappingsData]);


  useEffect(() => {
    if (settings) {
      setFormData({
        companyName: settings.companyName || '',
        companyPhone: settings.companyPhone || '',
        companyPhone2: settings.companyPhone2 || '',
        companyEmail: settings.companyEmail || '',
        companyCvr: settings.companyCvr || '',
        companyAddress: settings.companyAddress || '',
        companyCity: settings.companyCity || '',
        companyPostalCode: settings.companyPostalCode || '',
        companyCountry: settings.companyCountry || 'Danmark',
        companyLogoUrl: settings.companyLogoUrl || '',
        autoUploadToDora: settings.autoUploadToDora,
        autoUploadFromDate: settings.autoUploadFromDate || '',
        defaultTemplateId: settings.defaultTemplateId || 'komplet',
        splitRouteTeamId: settings.splitRouteTeamId || '',
        danlonSalaryType: settings.danlonSalaryType || '',
        datalonVirksomhedsNr: settings.datalonVirksomhedsNr || '',
        datalonLoentermin: settings.datalonLoentermin || '',
        fmsApiKey: settings.fmsApiKey || '',
        fmsCustomerId: settings.fmsCustomerId || '',
        costAllocationMethod: settings.costAllocationMethod || 'REVENUE',
        economicAgreementToken: settings.economicAgreementToken || '',
        economicDefaultJournal: settings.economicDefaultJournal ?? null,
      });
      setAutoUploadTemplates(settings.autoUploadTemplates || []);
      setTermsLinks(settings.termsLinks || []);
      // Fetch logo preview if there's an uploaded logo
      if (settings.companyLogoMime) {
        api.getLogoBlob().then(blob => {
          if (blob) {
            // Revoke old URL if exists
            if (logoPreviewUrl) {
              URL.revokeObjectURL(logoPreviewUrl);
            }
            setLogoPreviewUrl(URL.createObjectURL(blob));
          }
        });
      }
    }
    // Cleanup blob URL on unmount
    return () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [settings]);

  const handleChange = (field: keyof OrganizationSettings, value: string | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleTemplateAutoUploadToggle = (templateId: string, checked: boolean) => {
    setAutoUploadTemplates(prev => {
      if (checked) {
        return [...prev, templateId];
      } else {
        return prev.filter(id => id !== templateId);
      }
    });
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      ...formData,
      autoUploadTemplates,
      termsLinks: termsLinks.filter(l => l.trim().length > 0),
    });
  };

  const handleSaveMappings = async () => {
    setIsSavingMappings(true);
    try {
      await api.updateDriverMappings(driverMappings);
      queryClient.invalidateQueries({ queryKey: ['driver-mappings'] });
      setDanlonHasChanges(false);
      setSaveMessage({ type: 'success', text: 'Chauffør-mappings gemt!' });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch {
      setSaveMessage({ type: 'error', text: 'Kunne ikke gemme chauffør-mappings' });
      setTimeout(() => setSaveMessage(null), 3000);
    } finally {
      setIsSavingMappings(false);
    }
  };

  const handleTestEconomicConnection = async () => {
    setIsTestingEconomic(true);
    setEconomicTestResult(null);
    try {
      const result = await api.testEconomicConnection();
      setEconomicTestResult(result);
      if (result.success) {
        // Load journals on successful connection
        try {
          const journals = await api.getEconomicJournals();
          setEconomicJournals(journals);
          setEconomicJournalsLoaded(true);
        } catch {
          // journals load failed silently
        }
      }
    } catch {
      setEconomicTestResult({ success: false, message: 'Forbindelse fejlede' });
    } finally {
      setIsTestingEconomic(false);
    }
  };

  const handleCopyWebhookUrl = () => {
    if (settings?.economicWebhookSecret) {
      const url = `${window.location.origin}/api/economic/webhook/${settings.economicWebhookSecret}`;
      navigator.clipboard.writeText(url);
      setSaveMessage({ type: 'success', text: 'Webhook-URL kopieret!' });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const handleMappingChange = (driverId: string, field: 'employeeNumber' | 'datalonEmployeeNumber' | 'fmsDeviceId', value: string) => {
    setDriverMappings(prev =>
      prev.map(m => m.driverId === driverId ? { ...m, [field]: value } : m)
    );
    setDanlonHasChanges(true);
  };

  // Bulk upload handlers
  const startBulkUpload = async () => {
    if (!bulkFromDate || !bulkTemplateId) return;

    try {
      const jobId = await api.startBulkUploadJob(bulkFromDate, bulkTemplateId, bulkSkipExisting);
      setBulkJobId(jobId);
      startPolling(jobId);
    } catch (error) {
      setSaveMessage({ type: 'error', text: 'Kunne ikke starte bulk upload' });
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const startPolling = (jobId: string) => {
    // Clear any existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    // Poll immediately
    pollJobStatus(jobId);

    // Then poll every 2 seconds
    pollingRef.current = setInterval(() => {
      pollJobStatus(jobId);
    }, 2000);
  };

  const pollJobStatus = async (jobId: string) => {
    try {
      const status = await api.getBulkUploadJobStatus(jobId);
      setBulkJobStatus(status);

      // Stop polling when job is done
      if (status.status === 'completed' || status.status === 'failed') {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    } catch (error) {
      console.error('Failed to poll job status:', error);
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Load existing running job on mount
  useEffect(() => {
    const loadExistingJob = async () => {
      try {
        const jobs = await api.getBulkUploadJobs();
        const runningJob = jobs.find(j => j.status === 'running' || j.status === 'pending');
        if (runningJob) {
          setBulkJobId(runningJob.id);
          setBulkJobStatus(runningJob);
          startPolling(runningJob.id);
        }
      } catch (error) {
        console.error('Failed to load existing bulk upload jobs:', error);
      }
    };
    loadExistingJob();
  }, []);

  const resetBulkUpload = () => {
    setBulkJobId(null);
    setBulkJobStatus(null);
    setBulkFromDate('');
    setBulkTemplateId('');
  };

  const isBulkJobRunning = bulkJobStatus?.status === 'running' || bulkJobStatus?.status === 'pending';

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Indstillinger</Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Synkroniser data fra DORA (teams, chauffører, trailere)">
            <Button
              variant="outlined"
              startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={handleSyncFromDora}
              disabled={isSyncing}
              size="small"
            >
              {isSyncing ? 'Synkroniserer...' : 'Synk fra DORA'}
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
            size="small"
          >
            {updateMutation.isPending ? 'Gemmer...' : 'Gem'}
          </Button>
        </Box>
      </Box>

      {saveMessage && (
        <Alert severity={saveMessage.type} sx={{ mb: 1.5 }}>
          {saveMessage.text}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          <Tab label="Firma" />
          <Tab label="Køresedler" />
          <Tab label="Chauffører" />
          <Tab label="CMR Fragtbrev" />
          <Tab label="Integrationer" />
          <Tab label="Dataløn Branche" />
        </Tabs>
      </Box>

      {activeTab === 0 && (
      <Grid container spacing={2}>
        {/* Company Master Data */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Firmastamdata
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Vises på køresedler
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Firmanavn"
                    value={formData.companyName || ''}
                    onChange={(e) => handleChange('companyName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="CVR"
                    value={formData.companyCvr || ''}
                    onChange={(e) => handleChange('companyCvr', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.companyEmail || ''}
                    onChange={(e) => handleChange('companyEmail', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Telefon"
                    value={formData.companyPhone || ''}
                    onChange={(e) => handleChange('companyPhone', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Telefon 2"
                    value={formData.companyPhone2 || ''}
                    onChange={(e) => handleChange('companyPhone2', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Adresse"
                    value={formData.companyAddress || ''}
                    onChange={(e) => handleChange('companyAddress', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Postnr."
                    value={formData.companyPostalCode || ''}
                    onChange={(e) => handleChange('companyPostalCode', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="By"
                    value={formData.companyCity || ''}
                    onChange={(e) => handleChange('companyCity', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Land"
                    value={formData.companyCountry || ''}
                    onChange={(e) => handleChange('companyCountry', e.target.value)}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Logo Section */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Firmalogo
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                Upload logo til køresedler (maks. 2MB, JPEG/PNG/GIF/WebP)
              </Typography>

              {/* Hidden file input */}
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
                onChange={handleLogoUpload}
              />

              <Box display="flex" gap={1} alignItems="center">
                <Button
                  variant="outlined"
                  startIcon={isUploadingLogo ? <CircularProgress size={16} /> : <ImageIcon />}
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isUploadingLogo}
                >
                  {logoPreviewUrl ? 'Skift logo' : 'Upload logo'}
                </Button>
                {logoPreviewUrl && (
                  <Tooltip title="Slet logo">
                    <IconButton
                      color="error"
                      onClick={handleDeleteLogo}
                      disabled={isUploadingLogo}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {logoPreviewUrl && (
                <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
                  <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                    Nuværende logo:
                  </Typography>
                  <img
                    src={logoPreviewUrl}
                    alt="Logo preview"
                    style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Handelsbetingelser links */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Handelsbetingelser
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                Links til jeres handelsbetingelser. Vises altid på tilbuds-PDF. Tilføj flere hvis I har separate sider for fx transport og udlejning.
              </Typography>
              {termsLinks.length === 0 && (
                <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>
                  Ingen links tilføjet endnu.
                </Typography>
              )}
              {termsLinks.map((link, i) => (
                <Box key={i} display="flex" gap={1} alignItems="center" mb={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="https://eksempel.dk/handelsbetingelser/"
                    value={link}
                    onChange={(e) => {
                      const v = e.target.value;
                      setTermsLinks(prev => prev.map((l, j) => j === i ? v : l));
                      setHasChanges(true);
                    }}
                  />
                  <IconButton size="small" onClick={() => {
                    setTermsLinks(prev => prev.filter((_, j) => j !== i));
                    setHasChanges(true);
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  setTermsLinks(prev => [...prev, '']);
                  setHasChanges(true);
                }}
              >
                Tilføj link
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Economy Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Økonomi
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Indstillinger for omkostningsfordeling
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Omkostningsfordeling på ordrer</InputLabel>
                <Select
                  value={formData.costAllocationMethod || 'REVENUE'}
                  label="Omkostningsfordeling på ordrer"
                  onChange={(e) => handleChange('costAllocationMethod', e.target.value)}
                >
                  <MenuItem value="EQUAL">Ligeligt fordelt</MenuItem>
                  <MenuItem value="REVENUE">Omsætningsvægtet</MenuItem>
                  <MenuItem value="LOADMETER_KM">Læssemeter × km</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Bestemmer hvordan ruteomkostninger fordeles på de enkelte ordrer i ordreregnskabet.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {activeTab === 1 && (
      <Grid container spacing={2}>
        {/* Køreseddel Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Køreseddel-indstillinger
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Standardindstillinger
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Standard skabelon</InputLabel>
                    <Select
                      value={formData.defaultTemplateId || 'komplet'}
                      label="Standard skabelon"
                      onChange={(e) => handleChange('defaultTemplateId', e.target.value)}
                    >
                      {templates?.map((template: KoreseddelTemplate) => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.autoUploadToDora || false}
                        onChange={(e) => handleChange('autoUploadToDora', e.target.checked)}
                      />
                    }
                    label="Auto-upload alle til DORA"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                    Upload automatisk ALLE genererede køresedler til DORA TMS
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} sx={{ ml: 6 }}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Auto-upload fra dato"
                    value={formData.autoUploadFromDate || ''}
                    onChange={(e) => handleChange('autoUploadFromDate', e.target.value || null)}
                    InputLabelProps={{ shrink: true }}
                    helperText="Kun auto-upload køresedler for ordrer med afhentning på eller efter denne dato. Lad stå tom for ingen begrænsning."
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Næste køreseddelnummer"
                    value={settings?.nextKoreseddelNumber || 1}
                    disabled
                    helperText="Nummeret øges automatisk ved hver genereret køreseddel"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Route Split Settings */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Rutedelings-indstillinger
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Indstillinger for deling af ordrer og ruter
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Team for 1/3-ruter (originalordrer)</InputLabel>
                <Select
                  value={formData.splitRouteTeamId || ''}
                  label="Team for 1/3-ruter (originalordrer)"
                  onChange={(e) => handleChange('splitRouteTeamId', e.target.value || null)}
                >
                  <MenuItem value="">
                    <em>Intet team</em>
                  </MenuItem>
                  {teams?.map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Ved deling af ordrer eller ruter vil ruten med originalordrer (1/3) blive tildelt dette team.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Template List with Auto-Upload Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Skabeloner & Auto-Upload
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Vælg skabeloner til automatisk upload til DORA
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Skabelon</TableCell>
                      <TableCell>Beskrivelse</TableCell>
                      <TableCell align="center">Layout</TableCell>
                      <TableCell align="center">Beløb</TableCell>
                      <TableCell align="center">Preview</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Auto-upload til DORA når denne skabelon bruges">
                          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                            <CloudUploadIcon fontSize="small" />
                            Auto-upload
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templates?.map((template: KoreseddelTemplate) => (
                      <TableRow key={template.id} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <DescriptionIcon fontSize="small" color="action" />
                            <Typography variant="body2" fontWeight={500}>
                              {template.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {template.description}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={template.layout === 'two-column' ? '2 kolonner' : '1 kolonne'}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {template.sections.showAmounts ? (
                            <Chip label="Ja" size="small" color="success" variant="outlined" />
                          ) : (
                            <Chip label="Nej" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Se eksempel-PDF">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={previewLoading === template.id ? <CircularProgress size={16} /> : <VisibilityIcon />}
                              onClick={() => handleViewTemplatePreview(template.id)}
                              disabled={previewLoading !== null}
                            >
                              {previewLoading === template.id ? 'Henter...' : 'Se'}
                            </Button>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Checkbox
                            checked={autoUploadTemplates.includes(template.id)}
                            onChange={(e) => handleTemplateAutoUploadToggle(template.id, e.target.checked)}
                            disabled={formData.autoUploadToDora}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {formData.autoUploadToDora && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  "Auto-upload alle" er aktiveret - alle skabeloner uploades automatisk til DORA.
                </Alert>
              )}

              {autoUploadTemplates.length > 0 && !formData.autoUploadToDora && (
                <Box mt={2}>
                  <Typography variant="body2" color="text.secondary">
                    {autoUploadTemplates.length} skabelon(er) valgt til auto-upload
                  </Typography>
                </Box>
              )}

              <Alert severity="info" sx={{ mt: 2 }}>
                Auto-upload gælder kun for <strong>fremtidige ordrer</strong>. Brug "Retroaktiv Upload" nedenfor for at uploade køresedler til historiske ordrer.
              </Alert>

              {hasChanges && (
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                  >
                    {updateMutation.isPending ? 'Gemmer...' : 'Gem ændringer'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Retroactive Bulk Upload */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <HistoryIcon color="action" sx={{ fontSize: 18 }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  Retroaktiv Upload
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Generer og upload køresedler for historiske ordrer (1 sek. mellemrum)
              </Typography>

              {!bulkJobId ? (
                <Grid container spacing={1.5} alignItems="flex-end">
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Fra dato"
                      value={bulkFromDate}
                      onChange={(e) => setBulkFromDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      helperText="Ordrer fra denne dato til i dag"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth>
                      <InputLabel>Skabelon</InputLabel>
                      <Select
                        value={bulkTemplateId}
                        label="Skabelon"
                        onChange={(e) => setBulkTemplateId(e.target.value)}
                      >
                        {templates?.map((template: KoreseddelTemplate) => (
                          <MenuItem key={template.id} value={template.id}>
                            {template.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={bulkSkipExisting}
                          onChange={(e) => setBulkSkipExisting(e.target.checked)}
                        />
                      }
                      label="Spring over eksisterende"
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={startBulkUpload}
                      disabled={!bulkFromDate || !bulkTemplateId}
                      startIcon={<CloudUploadIcon />}
                    >
                      Start
                    </Button>
                  </Grid>
                </Grid>
              ) : (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2">
                        {bulkJobStatus?.processed || 0} / {bulkJobStatus?.total || 0} ordrer behandlet
                        {bulkJobStatus?.failed ? ` (${bulkJobStatus.failed} fejlede)` : ''}
                      </Typography>
                      <Chip
                        label={
                          bulkJobStatus?.status === 'running' ? 'Kører...' :
                          bulkJobStatus?.status === 'completed' ? 'Færdig' :
                          bulkJobStatus?.status === 'failed' ? 'Fejlet' : 'Venter'
                        }
                        color={
                          bulkJobStatus?.status === 'completed' ? 'success' :
                          bulkJobStatus?.status === 'failed' ? 'error' : 'default'
                        }
                        size="small"
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={bulkJobStatus?.total ? (bulkJobStatus.processed / bulkJobStatus.total) * 100 : 0}
                    />
                  </Box>

                  {bulkJobStatus?.status === 'completed' && (
                    <Alert
                      severity={bulkJobStatus.failed > 0 ? 'warning' : 'success'}
                      sx={{ mb: 2 }}
                      action={
                        <Box display="flex" gap={1}>
                          {bulkJobStatus.failed > 0 && (
                            <Button
                              size="small"
                              color="inherit"
                              onClick={() => setShowErrorsDialog(true)}
                            >
                              Vis fejl ({bulkJobStatus.failed})
                            </Button>
                          )}
                          <Button size="small" color="inherit" onClick={resetBulkUpload}>
                            Nulstil
                          </Button>
                        </Box>
                      }
                    >
                      Færdig! {bulkJobStatus.processed - bulkJobStatus.failed} køresedler uploadet.
                    </Alert>
                  )}

                  {bulkJobStatus?.status === 'failed' && (
                    <Alert severity="error" sx={{ mb: 2 }} action={
                      <Button size="small" color="inherit" onClick={resetBulkUpload}>
                        Prøv igen
                      </Button>
                    }>
                      Job fejlede uventet.
                    </Alert>
                  )}

                  {isBulkJobRunning && (
                    <Typography variant="body2" color="text.secondary">
                      Vent venligst... Upload kører i baggrunden med 1 sekunds mellemrum.
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>
      )}

      {activeTab === 2 && (
      <Grid container spacing={2}>
        {/* Chauffør-mappings (Danløn + FMS) */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Chauffør-mappings
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Map DORA-chauffører til Danløn medarbejdernumre og FMS-trackere
              </Typography>

              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Danløn løntype-kode"
                    value={formData.danlonSalaryType || ''}
                    onChange={(e) => handleChange('danlonSalaryType', e.target.value)}
                    helperText="F.eks. 0100"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Dataløn virksomhedsnr"
                    value={formData.datalonVirksomhedsNr || ''}
                    onChange={(e) => handleChange('datalonVirksomhedsNr', e.target.value)}
                    helperText="8-cifret (fx 00300284)"
                    size="small"
                    inputProps={{ maxLength: 8 }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Dataløn løntermin"
                    value={formData.datalonLoentermin || ''}
                    onChange={(e) => handleChange('datalonLoentermin', e.target.value)}
                    helperText="Terminkode fra Dataløn, fx 35"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="FMS API-nøgle"
                    type="password"
                    value={formData.fmsApiKey || ''}
                    onChange={(e) => handleChange('fmsApiKey', e.target.value)}
                    helperText="Fleet Management System"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField
                    fullWidth
                    label="FMS Kunde-ID"
                    value={formData.fmsCustomerId || ''}
                    onChange={(e) => handleChange('fmsCustomerId', e.target.value)}
                    helperText="Kunde-ID i FMS"
                    size="small"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {driverMappings.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Chauffør</TableCell>
                        <TableCell sx={{ width: 160 }}>Danløn medarbejdernr.</TableCell>
                        <TableCell sx={{ width: 160 }}>Dataløn medarbejdernr.</TableCell>
                        <TableCell sx={{ width: 220 }}>FMS Tracker</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {driverMappings.map((mapping) => (
                        <TableRow key={mapping.driverId} hover>
                          <TableCell>{mapping.driverName}</TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={mapping.employeeNumber}
                              onChange={(e) => handleMappingChange(mapping.driverId, 'employeeNumber', e.target.value)}
                              placeholder="F.eks. 1234"
                              variant="outlined"
                              sx={{ '& .MuiInputBase-input': { py: 0.5, px: 1 } }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={mapping.datalonEmployeeNumber || ''}
                              onChange={(e) => handleMappingChange(mapping.driverId, 'datalonEmployeeNumber', e.target.value)}
                              placeholder={mapping.employeeNumber || '—'}
                              variant="outlined"
                              sx={{ '& .MuiInputBase-input': { py: 0.5, px: 1 } }}
                            />
                          </TableCell>
                          <TableCell>
                            {fmsDevices && fmsDevices.length > 0 ? (
                              <FormControl size="small" fullWidth>
                                <Select
                                  value={mapping.fmsDeviceId || ''}
                                  onChange={(e) => handleMappingChange(mapping.driverId, 'fmsDeviceId', e.target.value)}
                                  displayEmpty
                                  sx={{ '& .MuiSelect-select': { py: 0.5 } }}
                                >
                                  <MenuItem value="">
                                    <em>Ingen</em>
                                  </MenuItem>
                                  {fmsDevices.map((device) => (
                                    <MenuItem key={device.id} value={device.id}>
                                      {device.name}{device.vehicleRegistration ? ` (${device.vehicleRegistration})` : ''}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              <TextField
                                size="small"
                                value={mapping.fmsDeviceId || ''}
                                onChange={(e) => handleMappingChange(mapping.driverId, 'fmsDeviceId', e.target.value)}
                                placeholder={settings?.fmsApiKey ? 'Henter...' : 'Konfigurer FMS først'}
                                variant="outlined"
                                disabled={!settings?.fmsApiKey}
                                sx={{ '& .MuiInputBase-input': { py: 0.5, px: 1 } }}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Ingen chauffører fundet. Synkroniser fra DORA først.
                </Alert>
              )}

              {danlonHasChanges && (
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    startIcon={isSavingMappings ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={handleSaveMappings}
                    disabled={isSavingMappings}
                  >
                    {isSavingMappings ? 'Gemmer...' : 'Gem mappings'}
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>
      )}

      {activeTab === 3 && (
      <Grid container spacing={2}>
        {/* CMR Field Mapping */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                <DescriptionIcon color="action" sx={{ fontSize: 18 }} />
                <Typography variant="subtitle2" fontWeight={600}>
                  CMR Fragtbrev — Felt-mapping
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Konfigurer hvordan CMR-felter udfyldes med data fra ordrer
              </Typography>
              <CmrSettings />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {activeTab === 4 && (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                e-conomic
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Forbind til e-conomic for at trække leverandørfakturaer
              </Typography>

              <Grid container spacing={1.5}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 0 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Har du ikke et agreement token endnu?
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      href="https://secure.e-conomic.com/secure/api1/requestaccess.aspx?appPublicToken=6VyrCkZICFcbXBVRWqc2m0OUaBB8xJg70oirgm7kGXU"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Hent Agreement Token fra e-conomic
                    </Button>
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Agreement Grant Token"
                    type="password"
                    value={formData.economicAgreementToken || ''}
                    onChange={(e) => handleChange('economicAgreementToken', e.target.value)}
                    helperText={settings?.economicAgreementToken ? 'Token er gemt. Indtast nyt token for at opdatere.' : 'Indsæt dit e-conomic agreement token'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" gap={1} alignItems="center">
                    <Button
                      variant="outlined"
                      onClick={handleTestEconomicConnection}
                      disabled={isTestingEconomic || !formData.economicAgreementToken}
                      startIcon={isTestingEconomic ? <CircularProgress size={16} /> : <SyncIcon />}
                      size="small"
                    >
                      {isTestingEconomic ? 'Tester...' : 'Test forbindelse'}
                    </Button>
                    {economicTestResult && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        {economicTestResult.success ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CancelIcon color="error" fontSize="small" />
                        )}
                        <Typography variant="body2" color={economicTestResult.success ? 'success.main' : 'error.main'}>
                          {economicTestResult.message}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                {/* Standard journal-vælgeren er fjernet — vi bogfører ikke i e-conomic,
                    integrationen bruges kun til at trække leverandørfakturaer. */}

                {settings?.economicWebhookSecret && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                        Webhook-URL (kopier til e-conomic)
                      </Typography>
                      <Box display="flex" gap={1} alignItems="center">
                        <TextField
                          fullWidth
                          size="small"
                          value={`${window.location.origin}/api/economic/webhook/${settings.economicWebhookSecret}`}
                          InputProps={{ readOnly: true }}
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', fontFamily: 'monospace' } }}
                        />
                        <Tooltip title="Kopier webhook-URL">
                          <IconButton size="small" onClick={handleCopyWebhookUrl}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {activeTab === 5 && (
        <DatalonSettings
          driverMappings={driverMappings}
          onMappingChange={handleMappingChange}
          onSaveMappings={handleSaveMappings}
          mappingsDirty={danlonHasChanges}
          savingMappings={isSavingMappings}
        />
      )}

      {/* Error Details Dialog */}
      <Dialog open={showErrorsDialog} onClose={() => setShowErrorsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Fejlede ordrer</DialogTitle>
        <DialogContent>
          <List dense>
            {bulkJobStatus?.errors?.map((error, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={error.cargoRef || error.cargoId}
                  secondary={error.error}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorsDialog(false)}>Luk</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
